import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Hotels() {
  const [races, setRaces] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedRace, setSelectedRace] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [loadingRaces, setLoadingRaces] = useState(true);
  const [loadingHotels, setLoadingHotels] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadRaces() {
      setLoadingRaces(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Not authenticated. Please log in.');
          setRaces([]);
          return;
        }
        const res = await fetch('/api/races', { headers: { Authorization: `Bearer ${token}` } });
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('isAdmin');
          navigate('/');
          return;
        }
        if (!res.ok) {
          const txt = await res.text().catch(() => null);
          throw new Error(`Failed to load races: ${res.status}${txt ? ' - ' + txt.slice(0,200) : ''}`);
        }
        const data = await res.json().catch(() => null);
        setRaces(data || []);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoadingRaces(false);
      }
    }
    loadRaces();
  }, [navigate]);

  useEffect(() => {
    async function loadHotels() {
      if (!selectedRace) {
        setHotels([]);
        setSelectedHotel(null);
        return;
      }
      setLoadingHotels(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/races/${selectedRace}/hotels`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('isAdmin');
          navigate('/');
          return;
        }
        if (!res.ok) {
          const txt = await res.text().catch(() => null);
          throw new Error(`Failed to load hotels: ${res.status}${txt ? ' - ' + txt.slice(0,200) : ''}`);
        }
        const data = await res.json().catch(() => null);
  setHotels(data || []);
        // auto-select first hotel if any
        setSelectedHotel((data && data.length) ? data[0] : null);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoadingHotels(false);
      }
    }
    loadHotels();
  }, [selectedRace, navigate]);

  // Group identical hotels (same name + address) so UI shows unique hotels instead of one per-attendee reservation
  const groupedHotels = React.useMemo(() => {
    if (!hotels || !hotels.length) return [];
    const map = {};
    for (const h of hotels) {
      const key = `${h.hotel_name || ''}||${h.address || ''}`;
      if (!map[key]) {
        map[key] = { ...h, reservation_count: 0, reservation_ids: [] };
      }
      map[key].reservation_count += 1;
      map[key].reservation_ids.push(h.id);
    }
    return Object.values(map);
  }, [hotels]);

  const filteredRaces = races.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(s) || (r.location || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Hotels</h1>

      {error && <div className="mb-4 text-red-600">Error: {error}</div>}

      <div className="flex gap-4">
        {/* Left: races list (approx 1/3) */}
        <div className="w-1/3 border rounded p-3" style={{ maxHeight: '70vh' }}>
          <div className="mb-2">
            <input className="w-full border rounded p-2" placeholder="Search races by name or location" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="text-sm text-gray-600 mb-2">Races ({filteredRaces.length})</div>
          <div style={{ overflowY: 'auto', maxHeight: '60vh' }}>
            {loadingRaces ? (
              <div>Loading races...</div>
            ) : (
              filteredRaces.map(r => (
                <div key={r.id} className={`p-2 mb-1 rounded hover:bg-gray-100 cursor-pointer ${String(selectedRace) === String(r.id) ? 'bg-gray-200 font-semibold' : ''}`} onClick={() => setSelectedRace(r.id)}>
                  <div>{r.name}</div>
                  <div className="text-xs text-gray-500">{r.location} · {r.start_date ? new Date(r.start_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: hotels list + details (approx 2/3) */}
        <div className="flex-1">
          {!selectedRace ? (
            <div className="p-6 border rounded text-gray-600">Select a race on the left to view hotels.</div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              <div className="border rounded p-3">
                <div className="mb-2 flex justify-between items-center">
                  <div className="font-semibold">Hotels for selected race</div>

                </div>
                {loadingHotels ? (
                  <div>Loading hotels...</div>
                ) : hotels.length === 0 ? (
                  <div className="text-sm text-gray-600">No hotels for this race.</div>
                ) : (
                  <div>
                    <ul>
                        {groupedHotels.map(h => (
                          <li key={h.id} className={`p-2 rounded mb-1 hover:bg-gray-50 cursor-pointer ${selectedHotel && Number(selectedHotel.id) === Number(h.id) ? 'bg-blue-50 border-l-4 border-blue-400' : ''}`} onClick={() => setSelectedHotel(h)}>
                            <div className="flex justify-between">
                              <div className="font-medium text-sm">{h.hotel_name} {h.reservation_count > 1 ? <span className="text-xs text-gray-500">({h.reservation_count} reservations)</span> : null}</div>
                              <div className="text-xs text-gray-500">Owner: {h.user_id}</div>
                            </div>
                            <div className="text-xs text-gray-500">{h.check_in || '-'} → {h.check_out || '-'}</div>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="border rounded p-4">
                <div className="font-semibold mb-2">Hotel details</div>
                {!selectedHotel ? (
                  <div className="text-sm text-gray-600">Select a hotel from the list above to see details.</div>
                ) : (
                  <div>
                    <div className="text-xl font-bold mb-1">{selectedHotel.hotel_name}</div>
                    <div className="mb-2"><strong>Address:</strong> {selectedHotel.address || '-'}</div>
                    <div className="mb-2"><strong>Check-in:</strong> {selectedHotel.check_in || '-'}</div>
                    <div className="mb-2"><strong>Check-out:</strong> {selectedHotel.check_out || '-'}</div>
                    <div className="mb-2"><strong>Note:</strong> {selectedHotel.hotel_notes || '-'}</div>
                    <div className="mt-3">
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
